import axios from '@/plugins/axios';
import utils from '@/stores/utils';
import store from '@/stores/store';

const { loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => ({
    providers: [],
    providers_url: '/api/providers/',
    providers_filters: DEFAULT_FILTERS,
    providers_count: 0,
    providers_loaded: false,
    providers_loading: false,
    providers_loading_errors: null,
    cancelTokenSource: null,
    _storageOptions: options || {},
});

const mutations = {
    updateProviders(state, providers) {
        state.providers = providers;
        state.providers_loaded = true;
        state.providers_loading = false;
        state.providers_loading_errors = null;
    },
    updateProvidersUrl(state, providers_url) {
        state.providers_url = providers_url;
    },
    updateProvidersFilters(state, providers_filters) {
        state.providers_filters = providers_filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.providers_filters, fieldsToSave);
        }
    },
    updateProvidersLoading(state, providers_loading) {
        state.providers_loading = providers_loading;
    },
    updateProvidersLoadingErrors(state, providers_loading_errors) {
        state.providers_loading_errors = providers_loading_errors;
        state.providers_loading = false;
    },
    updateProvidersCount(state, providers_count) {
        state.providers_count = providers_count;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    async fetchProviders({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("core.view_provider") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateProvidersLoading', true);
        commit('updateProvidersLoadingErrors', null);

        try {
            const response = await axios.get(state.providers_url, {
                params: state.providers_filters,
                cancelToken: source.token,
            });
            commit('updateProviders', response.data.results);
            commit('updateProvidersCount', response.data.count);
            return response.data;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateProvidersLoadingErrors', error.details);
            throw error;
        } finally {
            commit('updateProvidersLoading', false);
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateProviders', []);
        
        // Update storage options if provided
        if (params.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateProvidersFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params.filters || {})
        });
        
        if (params.url) {
            commit('updateProvidersUrl', params.url);
        }
    },
};


export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});